import { Box, LoadingOverlay, Modal, Slider, Stack, Text } from '@mantine/core'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  EVENT_ID_PARAM_KEY,
  TRACKING_ID_PARAM_KEY
} from '@/components/events/helpers/searchParams'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useLinkCrossCameraTracks,
  useSearchCrossCameraLookalikes,
  useUnlinkCrossCameraTrack
} from '@/queries/crossCameraQueries'
import { showToast } from '@/theme/notifications'
import { CrudAction, Resource } from '@/types/permissions'
import { LookalikeList } from '../LookalikeList/LookalikeList'

type LookalikeSearchHandlerProps = {
  appId: string
  trackingId: string
  onClose: () => void
}

export const LookalikeSearchHandler = ({
  appId,
  trackingId,
  onClose
}: LookalikeSearchHandlerProps) => {
  const intl = useIntl()
  const [, setSearchParams] = useSearchParams()
  const [canLink] = useHasPermissions(Resource.Applications, [
    CrudAction.Update
  ])
  // The device acts at once; the platform hears of a join a few seconds later.
  const [samePersonByTrackletId, setSamePersonByTrackletId] = useState<
    Record<string, boolean>
  >({})
  const [minimumScore, setMinimumScore] = useState(0)

  const { data, isLoading, isError, error, refetch } =
    useSearchCrossCameraLookalikes({
      application_id: appId,
      tracking_id: trackingId
    })

  const {
    mutateAsync: linkTracks,
    isPending: isLinking,
    variables: linkVariables
  } = useLinkCrossCameraTracks()

  const {
    mutateAsync: unlinkTrack,
    isPending: isUnlinking,
    variables: unlinkVariables
  } = useUnlinkCrossCameraTrack()

  const shown =
    data?.results.filter((lookalike) => lookalike.score >= minimumScore) ?? []

  const pendingTrackletId = isLinking
    ? linkVariables?.other_tracking_id
    : isUnlinking
      ? unlinkVariables?.tracking_id
      : undefined

  const handleOpenEventClick = (eventId: string) => {
    // One update: react-router hands each setter the params from render, so two would overwrite.
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      next.set(EVENT_ID_PARAM_KEY, eventId)
      next.delete(TRACKING_ID_PARAM_KEY)
      return next
    })
    onClose()
  }

  const handleSamePersonClick = async (otherTrackingId: string) => {
    try {
      await linkTracks({
        application_id: appId,
        tracking_id: trackingId,
        other_tracking_id: otherTrackingId
      })
      setSamePersonByTrackletId((decided) => ({
        ...decided,
        [otherTrackingId]: true
      }))
      showToast(
        intl.formatMessage({ id: 'crossCamera.lookalikes.linked' }),
        'success'
      )
    } catch (linkError) {
      showToast(
        extractErrorMessage(
          linkError,
          intl.formatMessage({ id: 'crossCamera.lookalikes.linkError' })
        ),
        'error'
      )
    }
  }

  const handleNotSamePersonClick = async (otherTrackingId: string) => {
    try {
      await unlinkTrack({
        application_id: appId,
        tracking_id: otherTrackingId
      })
      setSamePersonByTrackletId((decided) => ({
        ...decided,
        [otherTrackingId]: false
      }))
      showToast(
        intl.formatMessage({ id: 'crossCamera.lookalikes.split' }),
        'success'
      )
    } catch (unlinkError) {
      showToast(
        extractErrorMessage(
          unlinkError,
          intl.formatMessage({ id: 'crossCamera.lookalikes.splitError' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="crossCamera.lookalikes.title" />}
      opened
      onClose={onClose}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          <FormattedMessage id="crossCamera.lookalikes.description" />
        </Text>

        {isLoading && (
          <Box pos="relative" mih={120}>
            <LoadingOverlay visible />
          </Box>
        )}

        {isError && (
          <ErrorWithReload onReload={() => void refetch()}>
            {extractErrorMessage(
              error,
              intl.formatMessage({ id: 'crossCamera.lookalikes.error' })
            )}
          </ErrorWithReload>
        )}

        {!!data?.results.length && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              <FormattedMessage id="crossCamera.lookalikes.minimum" />
            </Text>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={minimumScore}
              label={(value) => value.toFixed(2)}
              marks={[
                {
                  value: data.similarity_threshold,
                  label: data.similarity_threshold.toFixed(2)
                }
              ]}
              onChange={setMinimumScore}
            />
            <Text size="xs" c="dimmed" mt="md">
              <FormattedMessage
                id="crossCamera.lookalikes.shown"
                values={{ shown: shown.length, total: data.results.length }}
              />
            </Text>
          </Stack>
        )}

        {data && (
          <LookalikeList
            sourceIdentityId={data.source.identity_id}
            samePersonByTrackletId={samePersonByTrackletId}
            lookalikes={shown}
            threshold={data.similarity_threshold}
            canLink={canLink}
            pendingTrackletId={pendingTrackletId}
            onOpenEventClick={handleOpenEventClick}
            onSamePersonClick={(otherTrackingId) =>
              void handleSamePersonClick(otherTrackingId)
            }
            onNotSamePersonClick={(otherTrackingId) =>
              void handleNotSamePersonClick(otherTrackingId)
            }
          />
        )}
      </Stack>
    </Modal>
  )
}
